import * as React from 'react'
import {
	Alert,
	Box,
	Typography,
	Container,
	Paper,
	Button,
	Divider,
	CircularProgress,
	Stack,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import ImageDropzone from '../components/core/ImageDropzone'
import { ScaffoldRequestForm } from '../types/Scaffold/ScaffoldRequest'
import { createScaffoldRequest } from '../services/scaffoldRequestService'
import { useParams } from 'react-router-dom'
import { showErrorToast } from '../services/CommonServices/alertServices'
import { getFormDefinitionByKey } from '../services/formDefinitionsService'
import type { FormDefinitionDetail } from '../types/Form/FormDefinition'
import type { FormSubmissionRequest } from '../types/Form/FormSubmission'
import {
	buildDynamicFormSections,
	buildRequiredFieldErrors,
	buildSubmissionValues,
} from '../utils/dynamicFormsUtils'
import RequestFormSections, {
	FieldErrors,
	FieldValues,
} from '../components/scaffoldOrders/PublicRequest/RequestFormSections'
import RequestAttachmentList from '../components/scaffoldOrders/PublicRequest/RequestAttachmentList'
import RequestReceipt from '../components/scaffoldOrders/PublicRequest/RequestReceipt'

const SCAFFOLD_REQUEST_CODE = 'scaffold_request'

function shouldShowTagNumberField(values: FieldValues): boolean {
	const a = (values.action ?? '').trim()
	return a !== '' && a !== 'Erect'
}

const isSameFile = (a: File, b: File) =>
	a.name === b.name && a.size === b.size && a.lastModified === b.lastModified

export default function ScaffoldRequestPage() {
	const { projectId } = useParams()

	const [loading, setLoading] = React.useState(true)
	const [loadError, setLoadError] = React.useState<string | null>(null)
	const [requestForm, setRequestForm] = React.useState<FormDefinitionDetail | null>(null)
	const [fieldValues, setFieldValues] = React.useState<FieldValues>({})
	const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({})

	const [receipt, setReceipt] = React.useState<ScaffoldRequestForm | null>(null)
	const [submitting, setSubmitting] = React.useState(false)
	const [attachments, setAttachments] = React.useState<File[]>([])
	const [receiptFiles, setReceiptFiles] = React.useState<File[]>([])
	const [openAttachImages, setOpenAttachImages] = React.useState(false)

	const ensureDefaults = React.useCallback((formDetail: FormDefinitionDetail | null) => {
		if (!formDetail?.fields?.length) return
		setFieldValues((prev) => {
			const next = { ...prev }
			for (const f of formDetail.fields) {
				if (f.isHidden) continue
				if (next[f.fieldKey] === undefined) next[f.fieldKey] = ''
			}
			return next
		})
	}, [])

	const loadRequestForm = React.useCallback(async () => {
		if (!projectId) return
		setLoading(true)
		setLoadError(null)
		try {
			const detail = await getFormDefinitionByKey(SCAFFOLD_REQUEST_CODE, Number(projectId))
			setRequestForm(detail)
		} catch {
			setRequestForm(null)
			setLoadError('Could not load form configuration.')
		} finally {
			setLoading(false)
		}
	}, [projectId])

	React.useEffect(() => {
		loadRequestForm()
	}, [loadRequestForm])

	React.useEffect(() => {
		ensureDefaults(requestForm)
	}, [requestForm, ensureDefaults])

	const requestSections = React.useMemo(
		() => (requestForm ? buildDynamicFormSections(requestForm) : []),
		[requestForm],
	)

	const setRequestField = (key: string, value: string) => {
		setReceipt(null)
		const clearsTagNumber = key === 'action' && !shouldShowTagNumberField({ action: value })
		setFieldValues((p) => {
			const next = { ...p, [key]: value }
			if (clearsTagNumber) next.tagNumber = ''
			return next
		})
		setFieldErrors((p) => {
			const next = { ...p }
			if (next[key]) next[key] = undefined
			if (clearsTagNumber) next.tagNumber = undefined
			return next
		})
	}

	const validateForm = (): boolean => {
		if (!requestForm) return false

		const nextErrors: FieldErrors = buildRequiredFieldErrors(requestForm, fieldValues)
		delete nextErrors.tagNumber

		if (shouldShowTagNumberField(fieldValues) && !(fieldValues.tagNumber ?? '').trim()) {
			nextErrors.tagNumber = 'Tag Number is required for Modify/Dismantle actions'
		}

		const po = (fieldValues.po ?? '').trim()
		const wo = (fieldValues.workOrder ?? '').trim()
		if (po && wo && po === wo) {
			nextErrors.po = 'Purchase Order and Work Order cannot be the same'
			nextErrors.workOrder = 'Purchase Order and Work Order cannot be the same'
		}

		setFieldErrors(nextErrors)
		return Object.keys(nextErrors).filter((k) => nextErrors[k]).length === 0
	}

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault()
		if (!projectId) {
			showErrorToast({ message: 'Project ID is required' })
			return
		}
		if (!requestForm) {
			showErrorToast({ message: 'Form is not ready yet.' })
			return
		}
		if (!validateForm()) {
			return
		}

		let submissionValues: FormSubmissionRequest['values']
		try {
			const valuesForSubmit = shouldShowTagNumberField(fieldValues)
				? fieldValues
				: { ...fieldValues, tagNumber: '' }
			submissionValues = buildSubmissionValues(requestForm, valuesForSubmit)
		} catch (e: unknown) {
			const msg = e instanceof Error ? e.message : 'Invalid values.'
			showErrorToast({ message: msg })
			return
		}

		if (submissionValues.length === 0) {
			showErrorToast({ message: 'Nothing to submit.' })
			return
		}

		setSubmitting(true)
		try {
			const insertedRequest = await createScaffoldRequest(
				Number(projectId),
				requestForm.id,
				null,
				{
					values: submissionValues,
					specialFields: null,
				},
				attachments,
			)
			setReceipt(insertedRequest)
			setReceiptFiles(attachments)
			setAttachments([])
			setFieldErrors({})
			setFieldValues(() => {
				const cleared: FieldValues = {}
				for (const f of requestForm.fields) {
					if (!f.isHidden) cleared[f.fieldKey] = ''
				}
				return cleared
			})
		} catch (e: unknown) {
			const msg = e instanceof Error ? e.message : 'Submission failed.'
			showErrorToast({ message: msg })
		} finally {
			setSubmitting(false)
		}
	}

	const handleAddAttachments = (files: File[]) => {
		setAttachments((prev) => [
			...prev,
			...files.filter((file) => !prev.some((f) => isSameFile(f, file))),
		])
	}

	const handleDeleteAttachment = (file: File | undefined) => {
		if (!file) return
		setAttachments(attachments.filter((f) => f !== file))
	}

	const hasFieldErrors = Object.keys(fieldErrors).filter((k) => fieldErrors[k]).length > 0

	return (
		<Container maxWidth='md' sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
			<Box sx={{ mb: { xs: 3, sm: 4 } }}>
				<Stack spacing={1.5} sx={{ mb: { xs: 3, sm: 4 }, maxWidth: 640 }}>
					<Typography
						variant='h4'
						component='h1'
						sx={{
							fontWeight: 700,
							color: 'primary.main',
							fontSize: { xs: '1.5rem', sm: '2rem' },
							lineHeight: 1.25,
						}}
					>
						Scaffold Request Form
					</Typography>
					<Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.6 }}>
						Please fill out the form below to submit your scaffold request. Required fields are
						marked.
					</Typography>
				</Stack>

				<Paper
					elevation={0}
					sx={{
						p: { xs: 2.5, sm: 4, md: 5 },
						borderRadius: 2,
						bgcolor: 'grey.100',
						border: '1px solid',
						borderColor: 'divider',
						boxShadow: (theme) => theme.shadows[2],
					}}
				>
					<form onSubmit={handleSubmit}>
						{loading && (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
								<CircularProgress size={28} sx={{ m: 0, p: 0 }} />
							</Box>
						)}

						{!loading && loadError && (
							<Alert severity='warning' sx={{ mb: 2 }}>
								{loadError}
							</Alert>
						)}

						{!loading && !loadError && requestForm && (
							<Box sx={{ mb: { xs: 3, sm: 4 } }}>
								<RequestFormSections
									sections={requestSections}
									values={fieldValues}
									errors={fieldErrors}
									projectId={Number(projectId)}
									onFieldChange={setRequestField}
									fieldFilter={(f) =>
										f.fieldKey !== 'tagNumber' || shouldShowTagNumberField(fieldValues)
									}
								/>
							</Box>
						)}

						<Divider sx={{ my: { xs: 3, sm: 4 } }} />

						<RequestAttachmentList
							attachments={attachments}
							onDelete={handleDeleteAttachment}
							readOnly={submitting}
						/>

						<Stack
							direction={{ xs: 'column', sm: 'row' }}
							spacing={2}
							sx={{
								mt: { xs: 3, sm: 4 },
								justifyContent: 'flex-end',
								alignItems: { xs: 'stretch', sm: 'center' },
							}}
						>
							{!submitting && (
								<Button
									type='button'
									variant='outlined'
									color='primary'
									disabled={loading || Boolean(loadError) || !requestForm}
									onClick={() => setOpenAttachImages(true)}
									startIcon={<AttachFileIcon />}
									sx={{
										px: 3,
										py: 1.25,
										fontSize: '1rem',
										textTransform: 'none',
										color: 'primary.main',
									}}
								>
									Attach photos or PDFs
								</Button>
							)}
							<Button
								type='submit'
								variant='contained'
								color='success'
								disabled={submitting || loading || Boolean(loadError) || !requestForm}
								sx={{
									px: 4,
									py: 1.25,
									fontSize: '1rem',
									textTransform: 'none',
									minWidth: { sm: 200 },
								}}
							>
								{submitting ? 'Submitting...' : 'Submit request'}
							</Button>
						</Stack>

						{receipt && !hasFieldErrors && (
							<RequestReceipt receipt={receipt} files={receiptFiles} />
						)}
					</form>
				</Paper>
				<ImageDropzone
					setFiles={handleAddAttachments}
					externalOpen={openAttachImages}
					setExternalOpen={setOpenAttachImages}
					additionalFilesTypes={['.pdf']}
				/>
			</Box>
		</Container>
	)
}
